// Create-plan wizard. A new plan has 5 steps (Athlete / Protocol / Phases / Staff / Review). The
// Protocol step has a second view that lists the injury template library. A
// duplicate copies the phases, exit criteria, and staff of a source plan and opens on them first
// (Phases / Athlete / Staff / Review). All state is collected client-side — nothing hits the API
// until the final "Create plan" submit (POST plans, then ADD_PHASE, ADD_CRITERION, and PATCH).
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
	Button,
	Checkbox,
	DatePicker,
	Form,
	Input,
	InputNumber,
	Modal,
	Select,
	Space,
	Spin,
	Tag,
	Typography,
} from "antd";
import { CheckOutlined, CloseOutlined, FlagOutlined, LineChartOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { PlayerOptions } from "@components/Common/PlayerOptions";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { getRtpErrorMessage } from "@utils/rtp_utils";
import { DATE_FORMAT } from "@utils/date_utils";
import { coachName, rtpApi, useCoachList, useCurrentUserUuid, useRtpMetricOptions } from "./rtpHooks";
import { buildDetails, COMPARISON_OPTIONS, MetricStatsHint } from "./CriterionForm";
import { emptyStaffFlags, StaffFlagsEditor } from "./StaffFlagsEditor";
import { TemplateLibraryPane } from "./TemplateLibraryPane";
import type { RtpCriterionDetails, RtpPlanDetail, RtpStaffFlags, RtpTemplate } from "./types";

const { TextArea } = Input;

interface RtpInjury {
	injury_id: number;
	injury_type: string;
	injury_location: string;
	injury_start_date: string;
}

interface PlanFormValues {
	playerId: number;
	name: string;
	description?: string;
}

interface PhaseCriterionCard {
	key: string;
	criterionType: "event" | "metric";
	details: RtpCriterionDetails;
	allowedSigners: string[];
}

type DurationUnit = "days" | "weeks";

interface PhaseCard {
	key: string;
	name: string;
	goal: string;
	durationValue: number;
	durationUnit: DurationUnit;
	criteria: PhaseCriterionCard[];
}

type ProtocolKey = "broad" | "generic" | "custom" | "library";

const PROTOCOL_OPTIONS: { key: ProtocolKey; label: string; desc: string }[] = [
	{ key: "broad", label: "Broad Protocol", desc: "Structured phases with default durations" },
	{ key: "generic", label: "Generic", desc: "Simplified milestone-based approach" },
	{ key: "custom", label: "Custom", desc: "Blank shell — staff define everything" },
	{ key: "library", label: "Pre-made Injury Protocols", desc: "Start from a proven plan for common injuries or make it your own" },
];

// Weeks only — templates seed a starting shape; names, durations, and goals stay editable.
const PROTOCOL_TEMPLATES: Record<ProtocolKey, { name: string; durationValue: number; goal: string }[]> = {
	broad: [
		{
			name: "Acute / protection",
			durationValue: 2,
			goal: "Protect the injury, manage pain and swelling, and restore basic range of motion.",
		},
		{
			name: "Load introduction",
			durationValue: 3,
			goal: "Reintroduce controlled loading and rebuild movement quality and base strength.",
		},
		{
			name: "Progressive loading",
			durationValue: 4,
			goal: "Progressively build training load, strength, and conditioning toward pre-injury levels.",
		},
		{
			name: "Sport-specific",
			durationValue: 3,
			goal: "Reintegrate sport-specific drills, speed, and change of direction at full intensity.",
		},
	],
	generic: [
		{ name: "Early rehab", durationValue: 2, goal: "Settle symptoms and restore pain-free movement." },
		{ name: "Rebuild", durationValue: 4, goal: "Rebuild strength, capacity, and confidence under increasing load." },
		{
			name: "Return to performance",
			durationValue: 2,
			goal: "Return to full training and match readiness.",
		},
	],
	custom: [],
	library: [],
};

const DURATION_UNIT_OPTIONS: { value: DurationUnit; label: string }[] = [
	{ value: "days", label: "Days" },
	{ value: "weeks", label: "Weeks" },
];

const MANAGER_FLAGS: RtpStaffFlags = {
	CanManagePlan: true,
	CanCreateDayPlan: true,
	CanCreateExitCriteria: true,
};

const newPhaseCard = (): PhaseCard => ({
	key: crypto.randomUUID(),
	name: "",
	goal: "",
	durationValue: 1,
	durationUnit: "weeks",
	criteria: [],
});

const buildTemplatePhases = (protocol: ProtocolKey): PhaseCard[] =>
	PROTOCOL_TEMPLATES[protocol].map((t) => ({
		key: crypto.randomUUID(),
		name: t.name,
		goal: t.goal,
		durationValue: t.durationValue,
		durationUnit: "weeks" as const,
		criteria: [],
	}));

// A library template's phases as wizard cards. Durations are in weeks when they divide evenly. Each
// exit criteria gets the current user as signer, because a template holds no staff.
const buildLibraryPhases = (template: RtpTemplate, currentUserUuid: string): PhaseCard[] =>
	template.details.phases.map((phase) => ({
		key: crypto.randomUUID(),
		name: phase.name,
		goal: phase.goal,
		durationValue: phase.durationDays % 7 === 0 ? phase.durationDays / 7 : phase.durationDays,
		durationUnit: phase.durationDays % 7 === 0 ? ("weeks" as const) : ("days" as const),
		criteria: phase.criteria.map((criterion) => ({
			key: crypto.randomUUID(),
			criterionType: "event" as const,
			details: { description: criterion.description },
			allowedSigners: currentUserUuid ? [currentUserUuid] : [],
		})),
	}));

// A source plan's phases as wizard cards. Durations are the planned ones, in weeks when they divide
// evenly. Signers who are no longer active staff are dropped, because the backend rejects them. A
// criteria left with no signer gets the user who duplicates the plan.
const buildCopiedPhases = (plan: RtpPlanDetail, activeUuids: Set<string>, currentUserUuid: string): PhaseCard[] =>
	[...plan.phases]
		.sort((a, b) => a.order - b.order)
		.map((phase) => ({
			key: crypto.randomUUID(),
			name: phase.name,
			goal: phase.goal ?? "",
			durationValue: phase.plannedDurationDays % 7 === 0 ? phase.plannedDurationDays / 7 : phase.plannedDurationDays,
			durationUnit: phase.plannedDurationDays % 7 === 0 ? ("weeks" as const) : ("days" as const),
			criteria: phase.criteria.map((criterion) => {
				const signers = criterion.allowedSigners.filter((uuid) => activeUuids.has(uuid));
				return {
					key: crypto.randomUUID(),
					criterionType: criterion.criterionType,
					details: { ...criterion.details },
					allowedSigners: signers.length === 0 && currentUserUuid ? [currentUserUuid] : signers,
				};
			}),
		}));

// Ignores `key` on purpose — used to detect whether the phase list still matches what a template
// last seeded (see the seeding rule in the frontend design spec's step-2 section).
const phaseCardSignature = (cards: PhaseCard[]): string =>
	JSON.stringify(
		cards.map((c) => ({
			name: c.name,
			goal: c.goal,
			durationValue: c.durationValue,
			durationUnit: c.durationUnit,
			criteriaCount: c.criteria.length,
		}))
	);

// Order-insensitive deep-compare of the staff dict against its initial seed, to skip the PATCH
// when the staff step ends up matching what the creator started with (each flags object always
// has the same 3 keys in the same order, so JSON.stringify on the sorted entries is enough).
const canonicalStaff = (dict: Record<string, RtpStaffFlags>) =>
	JSON.stringify(Object.entries(dict).sort(([a], [b]) => a.localeCompare(b)));

const formatWeeks = (weeks: number): string => (Number.isInteger(weeks) ? String(weeks) : weeks.toFixed(1));

function getPhaseCardError(phases: PhaseCard[]): string | null {
	for (let i = 0; i < phases.length; i++) {
		const phase = phases[i];
		if (!phase.name.trim()) return `Phase ${i + 1} needs a name.`;
		for (let j = 0; j < phase.criteria.length; j++) {
			const criterion = phase.criteria[j];
			const label = `Phase ${i + 1}, exit criteria ${j + 1}`;
			if (!criterion.details.description.trim()) return `${label} needs a description.`;
			if (criterion.criterionType === "metric") {
				// No metricSource check: the backend does not store it, so copied criteria have none.
				const { metricKey, comparison, threshold } = criterion.details;
				if (!metricKey?.trim() || !comparison || threshold == null) {
					return `${label} needs a metric, comparison, and threshold.`;
				}
			}
			if (criterion.allowedSigners.length === 0) return `${label} needs at least one staff member who can sign off.`;
		}
	}
	return null;
}

interface DurationFieldProps {
	value: number | null;
	unit: DurationUnit;
	onValueChange: (value: number | null) => void;
	onUnitChange: (unit: DurationUnit) => void;
	size?: "small" | "middle";
}

// Shared by the step-1 "expected rehab duration" field and each phase card's duration control.
const DurationField = ({ value, unit, onValueChange, onUnitChange, size = "middle" }: DurationFieldProps) => (
	<Space size={4}>
		<InputNumber min={1} precision={0} size={size} style={{ width: 70 }} value={value} onChange={onValueChange} />
		<Select size={size} style={{ width: 96 }} value={unit} onChange={onUnitChange} options={DURATION_UNIT_OPTIONS} />
	</Space>
);

// A patch to either the criterion's details, its signers, or both in one shot.
type CriterionPatch = Partial<RtpCriterionDetails> & { allowedSigners?: string[] };

interface CriterionRowProps {
	criterion: PhaseCriterionCard;
	onChange: (patch: CriterionPatch) => void;
	onRemove: () => void;
	coachOptions: { value: string; label: string }[];
	// The selected player. Metric rows show the player's values as a threshold hint.
	player?: { id: number; name: string };
}

// One row, either shape: the description, then (metric rows only) the condition with the
// player's values under it, then the signers.
const CriterionRow = ({ criterion, onChange, onRemove, coachOptions, player }: CriterionRowProps) => {
	const isMetric = criterion.criterionType === "metric";
	const { groups: metricGroups, byKey: metricByKey } = useRtpMetricOptions();
	return (
		<div className="rtp-wz-criterion-row">
			{isMetric ? (
				<LineChartOutlined className="rtp-wz-crit-icon rtp-wz-crit-icon--metric" />
			) : (
				<FlagOutlined className="rtp-wz-crit-icon rtp-wz-crit-icon--event" />
			)}
			<div className="rtp-wz-crit-body">
				<Input
					size="small"
					placeholder="What must be true to pass this exit criteria"
					value={criterion.details.description}
					onChange={(e) => onChange({ description: e.target.value })}
				/>
				{isMetric ? (
					<div className="rtp-wz-crit-line">
						<Select
							size="small"
							style={{ flex: 1 }}
							showSearch
							optionFilterProp="label"
							placeholder="Metric"
							options={metricGroups}
							value={criterion.details.metricKey || null}
							onChange={(v) => onChange({ metricKey: v, metricSource: metricByKey[v]?.source })}
						/>
						<Select
							size="small"
							style={{ width: 70 }}
							options={COMPARISON_OPTIONS}
							value={criterion.details.comparison}
							onChange={(v) => onChange({ comparison: v })}
						/>
						<InputNumber
							size="small"
							style={{ width: 90 }}
							placeholder="Threshold"
							value={criterion.details.threshold}
							onChange={(v) => onChange({ threshold: v ?? undefined })}
						/>
					</div>
				) : null}
				{isMetric && player ? (
					<MetricStatsHint
						player={player}
						metricKey={criterion.details.metricKey}
						applyTooltip="Click to use as the threshold"
						dataAttrPrefix="settings.rtp-plans.wizard-criterion"
						onApply={(value) => onChange({ threshold: value })}
					/>
				) : null}
				<Select
					mode="multiple"
					size="small"
					placeholder="Sign-off staff"
					options={coachOptions}
					value={criterion.allowedSigners}
					onChange={(v) => onChange({ allowedSigners: v })}
				/>
			</div>
			<Button
				type="text"
				danger
				size="small"
				icon={<CloseOutlined />}
				aria-label="Remove exit criteria"
				data-attr="settings.rtp-plans.wizard-criterion-remove"
				onClick={onRemove}
			/>
		</div>
	);
};

type WizardPane = "athlete" | "protocol" | "phases" | "staff" | "review";

const PANE_META: Record<WizardPane, { label: string; subtitle: string }> = {
	athlete: { label: "Athlete", subtitle: "Who is this plan for?" },
	protocol: { label: "Protocol", subtitle: "Choose a starting template" },
	phases: { label: "Phases", subtitle: "Structure the rehab" },
	staff: { label: "Staff", subtitle: "Who's involved" },
	review: { label: "Review", subtitle: "Confirm and create" },
};

// A duplicate shows what it copies first, and the source plan replaces the Protocol step.
const NEW_PLAN_PANES: WizardPane[] = ["athlete", "protocol", "phases", "staff", "review"];
const DUPLICATE_PANES: WizardPane[] = ["phases", "athlete", "staff", "review"];

interface WizardStepBarProps {
	labels: string[];
	step: number;
	onStepClick: (step: number) => void;
	disabled: boolean;
}

// Replaces antd's <Steps>. Done dots are the one sanctioned behavior addition here — clicking one
// jumps backward via the same setStep the footer's Back button already calls, no validation.
// Disabled (while submitting) drops the interactive affordances entirely, matching the footer's
// Back button being disabled at the same time.
const WizardStepBar = ({ labels, step, onStepClick, disabled }: WizardStepBarProps) => (
	<div className="rtp-wz-stepbar" role="group" aria-label="Wizard steps">
		{labels.map((label, index) => {
			const done = index < step;
			const active = index === step;
			const clickable = done && !disabled;
			return (
				<Fragment key={label}>
					<div className="rtp-wz-step">
						<div
							className={`rtp-wz-step-dot${active ? " active" : ""}${done ? " done" : ""}${clickable ? " clickable" : ""}`}
							role={clickable ? "button" : undefined}
							tabIndex={clickable ? 0 : undefined}
							aria-current={active ? "step" : undefined}
							aria-label={clickable ? `Go back to ${label}` : undefined}
							data-attr={clickable ? "settings.rtp-plans.wizard-step-jump" : undefined}
							onClick={clickable ? () => onStepClick(index) : undefined}
							onKeyDown={
								clickable
									? (e) => {
											if (e.key !== "Enter" && e.key !== " ") return;
											e.preventDefault();
											onStepClick(index);
										}
									: undefined
							}
						>
							{done ? <CheckOutlined /> : index + 1}
						</div>
						<span className={`rtp-wz-step-label${active ? " active" : ""}`}>{label}</span>
					</div>
					{index < labels.length - 1 ? <div className="rtp-wz-step-line" /> : null}
				</Fragment>
			);
		})}
	</div>
);

interface CreatePlanWizardProps {
	open: boolean;
	onClose: () => void;
	onOpenPlan: (id: number) => void;
	// Set to duplicate a plan: its phases, exit criteria, and staff seed the wizard.
	sourcePlanId?: number | null;
}

export const CreatePlanWizard = ({ open, onClose, onOpenPlan, sourcePlanId = null }: CreatePlanWizardProps) => {
	const { players, playersStatus, info } = useSelector(({ team }: any) => team);
	const teamName = info?.display_name;
	const filterByLabel = (input: string, option?: { label: string }) =>
		(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

	const coachList = useCoachList();
	const currentUserUuid = useCurrentUserUuid();
	const coachOptions = coachList.map((c) => ({ value: c.uuid, label: coachName(c) }));

	const [step, setStep] = useState(0);
	const [submitting, setSubmitting] = useState(false);
	const panes = sourcePlanId != null ? DUPLICATE_PANES : NEW_PLAN_PANES;
	const pane = panes[step];
	const nextStep = () => setStep((s) => s + 1);

	const [form] = Form.useForm<PlanFormValues>();
	const selectedPlayerId = Form.useWatch("playerId", form);
	const planName = Form.useWatch("name", form);
	const [planNameDirty, setPlanNameDirty] = useState(false);

	const [injuriesLoading, setInjuriesLoading] = useState(false);
	const [injuries, setInjuries] = useState<RtpInjury[]>([]);
	const [selectedInjuryKey, setSelectedInjuryKey] = useState<"general" | number | null>(null);
	const [injuryDate, setInjuryDate] = useState<Dayjs | null>(null);
	const injuryRequestId = useRef(0);

	const [expectedDurationValue, setExpectedDurationValue] = useState<number | null>(12);
	const [expectedDurationUnit, setExpectedDurationUnit] = useState<DurationUnit>("weeks");

	const [protocol, setProtocol] = useState<ProtocolKey>("broad");
	const [phases, setPhases] = useState<PhaseCard[]>([]);
	const seededSignatureRef = useRef<string | null>(null);

	// The library view of the Protocol step. Templates load again each time the wizard opens.
	const [protocolView, setProtocolView] = useState<"options" | "templates">("options");
	const [templates, setTemplates] = useState<RtpTemplate[]>([]);
	const [templatesStatus, setTemplatesStatus] = useState<"loading" | "ready" | "failed">("loading");
	const [templateKey, setTemplateKey] = useState<string | null>(null);
	const templatesRequestId = useRef(0);

	const [staff, setStaff] = useState<Record<string, RtpStaffFlags>>({});
	const initialStaffRef = useRef<Record<string, RtpStaffFlags>>({});

	// Duplicate mode: the source plan, and the copied phase cards, so the "Phases" and "Exit
	// criteria" checkboxes can take the copies out and put them back.
	const [sourceName, setSourceName] = useState<string | null>(null);
	const [sourceLoading, setSourceLoading] = useState(false);
	const [copyPhases, setCopyPhases] = useState(true);
	const [copyCriteria, setCopyCriteria] = useState(true);
	const copiedPhasesRef = useRef<PhaseCard[]>([]);
	const seededPhasesJsonRef = useRef<string | null>(null);
	const sourceRequestId = useRef(0);

	// "General" covers both the explicit General pill and "nothing picked yet" (used before the
	// step-0 Next guard forces a real choice).
	const injuryLabel = useMemo(() => {
		if (selectedInjuryKey === "general" || selectedInjuryKey === null) return "General";
		return injuries.find((inj) => inj.injury_id === selectedInjuryKey)?.injury_type ?? "General";
	}, [selectedInjuryKey, injuries]);

	useEffect(() => {
		if (!open) return;
		injuryRequestId.current++;
		form.resetFields();
		setStep(0);
		setPlanNameDirty(false);
		setInjuriesLoading(false);
		setInjuries([]);
		setSelectedInjuryKey(null);
		setInjuryDate(null);
		setExpectedDurationValue(12);
		setExpectedDurationUnit("weeks");
		setProtocol("broad");
		setProtocolView("options");
		setTemplateKey(null);
		setPhases([]);
		seededSignatureRef.current = null;
		const templatesId = ++templatesRequestId.current;
		setTemplates([]);
		setTemplatesStatus("loading");
		// A duplicate has no Protocol step, so it does not need the templates.
		if (sourcePlanId == null) {
			rtpApi
				.getRtpTemplates()
				.then((result) => {
					if (templatesId !== templatesRequestId.current) return;
					setTemplates(result);
					setTemplatesStatus("ready");
				})
				.catch((e: unknown) => {
					if (templatesId !== templatesRequestId.current) return;
					message.error(getRtpErrorMessage(e));
					setTemplatesStatus("failed");
				});
		}
		// The backend makes the creator the only manager, so this stays the PATCH baseline even when
		// a duplicate seeds more staff.
		const seed = currentUserUuid ? { [currentUserUuid]: { ...MANAGER_FLAGS } } : {};
		initialStaffRef.current = seed;
		setStaff(seed);
		setSourceName(null);
		setCopyPhases(true);
		setCopyCriteria(true);
		copiedPhasesRef.current = [];
		seededPhasesJsonRef.current = null;
		const requestId = ++sourceRequestId.current;
		setSourceLoading(sourcePlanId != null);
		if (sourcePlanId != null) {
			rtpApi
				.getRtpPlan(sourcePlanId)
				.then((plan: RtpPlanDetail) => {
					if (requestId !== sourceRequestId.current) return;
					const activeUuids = new Set([...coachList.map((c) => c.uuid), currentUserUuid]);
					const cards = buildCopiedPhases(plan, activeUuids, currentUserUuid);
					copiedPhasesRef.current = cards;
					seededPhasesJsonRef.current = JSON.stringify(cards);
					setPhases(cards);
					const copiedStaff = Object.fromEntries(
						Object.entries(plan.relevantStaff).filter(([uuid]) => activeUuids.has(uuid))
					);
					setStaff({ ...copiedStaff, ...seed });
					const totalDays = plan.phases.reduce((sum, phase) => sum + phase.plannedDurationDays, 0);
					setExpectedDurationValue(totalDays % 7 === 0 ? totalDays / 7 : totalDays);
					setExpectedDurationUnit(totalDays % 7 === 0 ? "weeks" : "days");
					setSourceName(plan.name);
				})
				.catch((e: unknown) => {
					if (requestId !== sourceRequestId.current) return;
					message.error(getRtpErrorMessage(e));
					onClose();
				})
				.finally(() => {
					if (requestId === sourceRequestId.current) setSourceLoading(false);
				});
		}
		// Full reset on every open, not on every currentUserUuid re-render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Auto-prefill the plan name from player + injury until the user types their own (an empty
	// name resumes auto-fill, since that's indistinguishable from "never touched it").
	useEffect(() => {
		if (planNameDirty || !selectedPlayerId || selectedInjuryKey === null) return;
		const player = (players ?? []).find((p: { player_id: number }) => p.player_id === selectedPlayerId);
		if (!player) return;
		form.setFieldValue("name", `${player.first_name} ${player.last_name} - ${injuryLabel}`);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPlayerId, selectedInjuryKey, planNameDirty, injuryLabel]);

	const handlePlayerChange = (value: number) => {
		const id = ++injuryRequestId.current;
		setSelectedInjuryKey(null);
		setInjuryDate(null);
		setInjuries([]);
		setInjuriesLoading(true);
		rtpApi
			.getInjuries({ player: value })
			.then((result: RtpInjury[]) => {
				if (id !== injuryRequestId.current) return;
				setInjuries(result ?? []);
			})
			.catch((e: unknown) => {
				if (id !== injuryRequestId.current) return;
				message.error(getRtpErrorMessage(e));
			})
			.finally(() => {
				if (id === injuryRequestId.current) setInjuriesLoading(false);
			});
	};

	const selectInjuryPill = (key: "general" | number) => {
		setSelectedInjuryKey(key);
		if (key === "general") {
			setInjuryDate(null);
			return;
		}
		const injury = injuries.find((inj) => inj.injury_id === key);
		if (injury) setInjuryDate(dayjs(injury.injury_start_date));
	};

	const getStaffName = (uuid: string): string => {
		const coach = coachList.find((c) => c.uuid === uuid);
		if (coach) return coachName(coach);
		return uuid === currentUserUuid ? "You" : uuid;
	};

	const addPhase = () => setPhases((prev) => [...prev, newPhaseCard()]);
	const removePhase = (key: string) => setPhases((prev) => prev.filter((p) => p.key !== key));
	const updatePhase = (key: string, patch: Partial<PhaseCard>) =>
		setPhases((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)));

	const addCriterion = (phaseKey: string, criterion: Omit<PhaseCriterionCard, "key">) =>
		setPhases((prev) =>
			prev.map((p) =>
				p.key === phaseKey
					? { ...p, criteria: [...p.criteria, { ...criterion, key: crypto.randomUUID() }] }
					: p
			)
		);
	const addEventCriterion = (phaseKey: string) =>
		addCriterion(phaseKey, { criterionType: "event", details: { description: "" }, allowedSigners: [] });
	const addMetricCriterion = (phaseKey: string) =>
		addCriterion(phaseKey, {
			criterionType: "metric",
			details: { description: "", metricSource: undefined, metricKey: "", comparison: ">=", threshold: undefined },
			allowedSigners: [],
		});

	// Exit criteria belong to phases, so unchecking "Phases" also unchecks "Exit criteria". The copies
	// come back without criteria; phases the user added stay as they are.
	const toggleCopyPhases = (checked: boolean) => {
		const copiedKeys = new Set(copiedPhasesRef.current.map((card) => card.key));
		setCopyPhases(checked);
		setCopyCriteria(false);
		setPhases((prev) => {
			const added = prev.filter((p) => !copiedKeys.has(p.key));
			return checked ? [...copiedPhasesRef.current.map((card) => ({ ...card, criteria: [] })), ...added] : added;
		});
	};

	// Takes the copied criteria out of the copied phases, or puts them back. Criteria the user added
	// to a phase are replaced; phases the user added do not change.
	const toggleCopyCriteria = (checked: boolean) => {
		const copiedCriteria = new Map(copiedPhasesRef.current.map((card) => [card.key, card.criteria]));
		setCopyCriteria(checked);
		setPhases((prev) =>
			prev.map((p) => {
				const criteria = copiedCriteria.get(p.key);
				if (!criteria) return p;
				return { ...p, criteria: checked ? criteria : [] };
			})
		);
	};

	const removeCriterion = (phaseKey: string, criterionKey: string) =>
		setPhases((prev) =>
			prev.map((p) =>
				p.key === phaseKey ? { ...p, criteria: p.criteria.filter((c) => c.key !== criterionKey) } : p
			)
		);

	const updateCriterion = (phaseKey: string, criterionKey: string, patch: CriterionPatch) => {
		const { allowedSigners, ...detailsPatch } = patch;
		setPhases((prev) =>
			prev.map((p) =>
				p.key !== phaseKey
					? p
					: {
							...p,
							criteria: p.criteria.map((c) =>
								c.key !== criterionKey
									? c
									: {
											...c,
											details: { ...c.details, ...detailsPatch },
											...(allowedSigners !== undefined ? { allowedSigners } : {}),
										}
							),
						}
			)
		);
	};

	const selectedTemplate = templates.find((t) => t.key === templateKey);

	const advanceFromProtocol = () => {
		const untouched =
			phases.length === 0 ||
			(seededSignatureRef.current !== null && phaseCardSignature(phases) === seededSignatureRef.current);
		const applyTemplate = () => {
			const seeded =
				protocol === "library" && selectedTemplate
					? buildLibraryPhases(selectedTemplate, currentUserUuid)
					: buildTemplatePhases(protocol);
			setPhases(seeded);
			seededSignatureRef.current = phaseCardSignature(seeded);
		};
		if (untouched) {
			applyTemplate();
			nextStep();
			return;
		}
		const label =
			protocol === "library" && selectedTemplate
				? selectedTemplate.name
				: (PROTOCOL_OPTIONS.find((p) => p.key === protocol)?.label ?? protocol);
		Modal.confirm({
			title: "Replace phases?",
			content: `Replace your phases with the ${label} template?`,
			okText: "Replace",
			okButtonProps: { "data-attr": "settings.rtp-plans.wizard-protocol-replace" } as any,
			onOk: () => {
				applyTemplate();
				nextStep();
			},
			onCancel: nextStep,
		});
	};

	const mergeCriterionSignersIntoStaff = () => {
		const signerUuids = new Set<string>();
		phases.forEach((p) => p.criteria.forEach((c) => c.allowedSigners.forEach((uuid) => signerUuids.add(uuid))));
		setStaff((prev) => {
			const next = { ...prev };
			signerUuids.forEach((uuid) => {
				if (!(uuid in next)) next[uuid] = emptyStaffFlags();
			});
			return next;
		});
	};

	// The library card opens the template view first. Only a chosen template moves on to the Phases step.
	const nextFromProtocol = () => {
		if (protocol === "library" && protocolView === "options") {
			setProtocolView("templates");
			return;
		}
		if (protocol === "library" && !selectedTemplate) {
			message.error("Select a template.");
			return;
		}
		advanceFromProtocol();
	};

	const goNext = async () => {
		if (pane === "athlete") {
			try {
				await form.validateFields();
			} catch {
				return;
			}
			if (selectedInjuryKey === null) {
				message.error("Select an injury.");
				return;
			}
		}
		if (pane === "protocol") {
			nextFromProtocol();
			return;
		}
		if (pane === "phases") {
			const error = getPhaseCardError(phases);
			if (error) {
				message.error(error);
				return;
			}
			mergeCriterionSignersIntoStaff();
		}
		nextStep();
	};

	const goBack = () => {
		if (pane === "protocol" && protocolView === "templates") {
			setProtocolView("options");
			return;
		}
		setStep((s) => Math.max(0, s - 1));
	};

	const handleCreate = async () => {
		let values: PlanFormValues;
		try {
			values = await form.validateFields();
		} catch {
			setStep(panes.indexOf("athlete"));
			return;
		}
		if (selectedInjuryKey === null) {
			message.error("Select an injury.");
			setStep(panes.indexOf("athlete"));
			return;
		}
		if (!Object.values(staff).some((flags) => flags.CanManagePlan)) {
			message.error("At least one staff member must manage the plan.");
			return;
		}

		setSubmitting(true);

		const expectedDurationDays =
			expectedDurationValue == null
				? null
				: expectedDurationUnit === "weeks"
					? expectedDurationValue * 7
					: expectedDurationValue;

		let planId: number;
		try {
			const created = await rtpApi.createRtpPlan({
				playerId: values.playerId,
				injuryId: selectedInjuryKey === "general" ? null : selectedInjuryKey,
				name: values.name,
				description: values.description,
				injuryDate: injuryDate ? injuryDate.format(DATE_FORMAT) : null,
				// Value in days.
				expectedDuration: expectedDurationDays,
				// For logging only: the plan this one was copied from, or null for a new plan.
				duplicatedFrom: sourcePlanId,
				// For logging only: the key of the library template, or null for any other protocol.
				templateId: protocol === "library" ? (selectedTemplate?.key ?? null) : null,
			});
			planId = created.id;
		} catch (e) {
			// Nothing was created (e.g. duplicate-per-injury 400) — stay in the wizard.
			message.error(getRtpErrorMessage(e));
			setSubmitting(false);
			return;
		}

		try {
			for (const phase of phases) {
				await rtpApi.rtpPlanAction(planId, {
					action: "ADD_PHASE",
					name: phase.name.trim(),
					goal: phase.goal.trim() || undefined,
					plannedDurationDays:
						phase.durationUnit === "weeks" ? phase.durationValue * 7 : phase.durationValue,
				});
			}

			if (phases.some((p) => p.criteria.length > 0)) {
				// Order = card index + 1 is deterministic here: every ADD_PHASE above appended with
				// no `order` key, on a plan that had no phases before this submit.
				const detail = await rtpApi.getRtpPlan(planId);
				const createdPhases = [...detail.phases].sort(
					(a: { order: number }, b: { order: number }) => a.order - b.order
				);
				if (createdPhases.length !== phases.length) {
					throw new Error("Plan created, but the phase list didn't match — exit criteria weren't attached.");
				}
				for (let i = 0; i < phases.length; i++) {
					const card = phases[i];
					if (card.criteria.length === 0) continue;
					const createdPhaseId = createdPhases[i].id;
					for (const criterion of card.criteria) {
						await rtpApi.rtpPhaseAction(createdPhaseId, {
							action: "ADD_CRITERION",
							criterionType: criterion.criterionType,
							details: buildDetails(criterion.details, criterion.criterionType),
							allowedSigners: criterion.allowedSigners,
						});
					}
				}
			}

			if (canonicalStaff(staff) !== canonicalStaff(initialStaffRef.current)) {
				await rtpApi.patchRtpPlan(planId, { relevantStaff: staff });
			}
		} catch (e) {
			// The plan itself exists — land on the draft with whatever succeeded, no rollback.
			// Prefer a locally-thrown Error's own message (e.g. the phase-count guard above);
			// an axios/network failure still goes through getRtpErrorMessage.
			const msg =
				e && typeof e === "object" && "response" in e
					? getRtpErrorMessage(e)
					: e instanceof Error && e.message
						? e.message
						: getRtpErrorMessage(e);
			message.error(msg);
			setSubmitting(false);
			onClose();
			onOpenPlan(planId);
			return;
		}

		setSubmitting(false);
		onClose();
		onOpenPlan(planId);
	};

	// A click outside the wizard, Esc, or × would lose the user's work, so ask first. An untouched
	// first step has nothing to lose and closes at once; for a duplicate, untouched means the copy
	// is still exactly as it was seeded.
	const requestClose = () => {
		const copyUntouched =
			sourcePlanId == null || sourceLoading || JSON.stringify(phases) === seededPhasesJsonRef.current;
		if (step === 0 && !form.isFieldsTouched() && copyUntouched) {
			onClose();
			return;
		}
		Modal.confirm({
			centered: true,
			title: "Discard this plan?",
			content: "You will lose what you entered in this wizard.",
			okText: "Discard",
			okType: "danger",
			cancelText: "Keep editing",
			okButtonProps: { "data-attr": "settings.rtp-plans.wizard-discard" } as any,
			cancelButtonProps: { "data-attr": "settings.rtp-plans.wizard-keep-editing" } as any,
			onOk: onClose,
		});
	};

	const selectedPlayer = (players ?? []).find((p: { player_id: number }) => p.player_id === selectedPlayerId);
	const hintPlayer = selectedPlayer
		? { id: selectedPlayer.player_id, name: `${selectedPlayer.first_name} ${selectedPlayer.last_name}` }
		: undefined;
	const protocolLabel = PROTOCOL_OPTIONS.find((p) => p.key === protocol)?.label ?? protocol;
	const libraryDisabled = templatesStatus !== "ready" || templates.length === 0;
	const sourceRow =
		protocol === "library"
			? { label: "Template", value: selectedTemplate?.name }
			: { label: "Protocol", value: protocolLabel };
	const totalWeeks = phases.reduce(
		(sum, p) => sum + (p.durationUnit === "weeks" ? p.durationValue : p.durationValue / 7),
		0
	);
	const totalWeeksLabel = formatWeeks(totalWeeks);
	const expectedWeeksLabel =
		expectedDurationValue == null
			? null
			: formatWeeks(expectedDurationUnit === "weeks" ? expectedDurationValue : expectedDurationValue / 7);
	const totalCriteriaCount = phases.reduce((sum, p) => sum + p.criteria.length, 0);
	const managerNames = Object.entries(staff)
		.filter(([, flags]) => flags.CanManagePlan)
		.map(([uuid]) => getStaffName(uuid));

	return (
		<Modal
			title={
				<div className="rtp-wz-title">
					<div className="rtp-wz-title-heading">{sourcePlanId != null ? "Duplicate RTP Plan" : "New RTP Plan"}</div>
					<div className="rtp-wz-title-subtitle">
						{pane === "protocol" && protocolView === "templates"
							? "Pick an injury template"
							: PANE_META[pane].subtitle}
					</div>
				</div>
			}
			open={open}
			onCancel={submitting ? undefined : requestClose}
			closable={!submitting}
			maskClosable={!submitting}
			destroyOnHidden
			width={850}
			style={{ top: 40 }}
			className="rtp-wz"
			footer={
				<Space>
					{step > 0 ? (
						<Button
							type="text"
							className="rtp-wz-back-btn"
							data-attr="settings.rtp-plans.create-back"
							disabled={submitting}
							onClick={goBack}
						>
							Back
						</Button>
					) : null}
					{step < panes.length - 1 ? (
						<Button
							type="primary"
							className="rtp-wz-primary-btn"
							data-attr="settings.rtp-plans.create-next"
							disabled={submitting || sourceLoading}
							onClick={goNext}
						>
							Next
						</Button>
					) : (
						<Button
							type="primary"
							className="rtp-wz-primary-btn"
							loading={submitting}
							data-attr="settings.rtp-plans.create-submit"
							onClick={handleCreate}
						>
							Create plan
						</Button>
					)}
				</Space>
			}
		>
			<WizardStepBar
				labels={panes.map((key) => PANE_META[key].label)}
				step={step}
				onStepClick={setStep}
				disabled={submitting}
			/>

			{/* Kept mounted (not conditionally rendered) so form.validateFields() still works at final submit. */}
			<div className="rtp-wz-pane" style={{ display: pane === "athlete" ? "block" : "none" }}>
				<Form form={form} layout="vertical">
					<Form.Item
						name="playerId"
						label="Player"
						rules={[{ required: true, message: "Select a player" }]}
					>
						<Select showSearch placeholder="Select player" onChange={handlePlayerChange} filterOption={filterByLabel}>
							{(players ?? []).map((player: any, i: number) =>
								PlayerOptions({ player, teamName, index: i, playersStatus })
							)}
						</Select>
					</Form.Item>

					<Form.Item label="Injury" required>
						{!selectedPlayerId ? (
							<Typography.Text type="secondary">Select a player first.</Typography.Text>
						) : (
							<div className="rtp-pills">
								<Tag.CheckableTag
									checked={selectedInjuryKey === "general"}
									onChange={() => selectInjuryPill("general")}
								>
									General
								</Tag.CheckableTag>
								{injuriesLoading ? (
									<Spin size="small" />
								) : (
									injuries.map((injury) => (
										<Tag.CheckableTag
											key={injury.injury_id}
											checked={selectedInjuryKey === injury.injury_id}
											onChange={() => selectInjuryPill(injury.injury_id)}
										>
											{`${injury.injury_type} — ${injury.injury_location} (${injury.injury_start_date})`}
										</Tag.CheckableTag>
									))
								)}
							</div>
						)}
					</Form.Item>

					<Form.Item label="Injury date">
						<DatePicker
							style={{ width: "100%" }}
							value={injuryDate}
							onChange={(date) => setInjuryDate(date)}
							disabled={!selectedPlayerId}
						/>
					</Form.Item>

					<Form.Item label="Expected rehab duration">
						<DurationField
							value={expectedDurationValue}
							unit={expectedDurationUnit}
							onValueChange={setExpectedDurationValue}
							onUnitChange={setExpectedDurationUnit}
						/>
					</Form.Item>

					<Form.Item
						name="name"
						label="Plan name"
						rules={[{ required: true, whitespace: true, message: "Enter a name" }]}
					>
						<Input placeholder="Plan name" onChange={(e) => setPlanNameDirty(e.target.value !== "")} />
					</Form.Item>

					<Form.Item name="description" label="Description">
						<TextArea rows={3} placeholder="Optional description" />
					</Form.Item>
				</Form>
			</div>

			{pane === "protocol" && protocolView === "options" ? (
				<div className="rtp-wz-pane" role="radiogroup" aria-label="Protocol type">
					{PROTOCOL_OPTIONS.map((option) => {
						const selected = protocol === option.key;
						const disabled = option.key === "library" && libraryDisabled;
						const select = () => {
							if (!disabled) setProtocol(option.key);
						};
						return (
							<div
								key={option.key}
								role="radio"
								aria-checked={selected}
								aria-disabled={disabled}
								tabIndex={disabled ? -1 : 0}
								className={`rtp-wz-protocol-card${selected ? " selected" : ""}${disabled ? " disabled" : ""}`}
								onClick={select}
								onKeyDown={(e) => {
									if (e.key !== "Enter" && e.key !== " ") return;
									e.preventDefault();
									select();
								}}
							>
								<div className={`rtp-wz-protocol-radio${selected ? " selected" : ""}`}>
									{selected ? <div className="rtp-wz-protocol-radio-dot" /> : null}
								</div>
								<div>
									<div className="rtp-wz-protocol-label">
										{option.label}{" "}
										{option.key === "library" ? (
											<Tag color="blue" bordered={false}>
												New
											</Tag>
										) : null}
									</div>
									<div className="rtp-wz-protocol-desc">{option.desc}</div>
								</div>
							</div>
						);
					})}
				</div>
			) : null}

			{pane === "protocol" && protocolView === "templates" ? (
				<TemplateLibraryPane templates={templates} selectedKey={templateKey} onSelect={setTemplateKey} />
			) : null}

			{pane === "phases" && sourceLoading ? (
				<div className="rtp-wz-pane rtp-wz-copy-loading">
					<Spin />
				</div>
			) : null}

			{pane === "phases" && !sourceLoading ? (
				<div className="rtp-wz-pane">
					{sourcePlanId != null ? (
						<div className="rtp-wz-copy-bar">
							<span>
								Copied from <strong>{sourceName}</strong>. Change anything you need.
							</span>
							{/* The counts are what the source plan holds, so they show what a checkbox copies. */}
							<div className="rtp-wz-copy-options">
								<Checkbox checked={copyPhases} onChange={(e) => toggleCopyPhases(e.target.checked)}>
									Phases ({copiedPhasesRef.current.length})
								</Checkbox>
								<Checkbox
									checked={copyCriteria}
									disabled={!copyPhases}
									onChange={(e) => toggleCopyCriteria(e.target.checked)}
								>
									Exit criteria ({copiedPhasesRef.current.reduce((sum, card) => sum + card.criteria.length, 0)})
								</Checkbox>
							</div>
						</div>
					) : null}
					{sourcePlanId != null && phases.length === 0 ? (
						<div className="rtp-wz-copy-empty">Nothing selected to copy over.</div>
					) : null}
					{phases.map((phase, index) => (
						<div className="rtp-wz-phase-card" key={phase.key}>
							<div className="rtp-wz-phase-header">
								<div className="rtp-wz-phase-num">{index + 1}</div>
								<Input
									variant="borderless"
									className="rtp-wz-phase-name-input"
									placeholder="Phase name"
									value={phase.name}
									onChange={(e) => updatePhase(phase.key, { name: e.target.value })}
								/>
								<DurationField
									size="small"
									value={phase.durationValue}
									unit={phase.durationUnit}
									onValueChange={(v) => updatePhase(phase.key, { durationValue: v ?? 1 })}
									onUnitChange={(u) => updatePhase(phase.key, { durationUnit: u })}
								/>
								<Button
									type="text"
									danger
									size="small"
									icon={<CloseOutlined />}
									aria-label="Remove phase"
									data-attr="settings.rtp-plans.wizard-phase-remove"
									onClick={() => removePhase(phase.key)}
								/>
							</div>
							<div className="rtp-wz-phase-body">
								<TextArea
									size="small"
									className="rtp-wz-phase-goal"
									autoSize={{ minRows: 1, maxRows: 4 }}
									placeholder="Phase description (optional)"
									value={phase.goal}
									onChange={(e) => updatePhase(phase.key, { goal: e.target.value })}
								/>
								<Typography.Text type="secondary" className="rtp-wz-section-label">
									Exit criteria ({phase.criteria.length} defined)
								</Typography.Text>
								<div className="rtp-wz-criteria-list">
									{phase.criteria.map((criterion) => (
										<CriterionRow
											key={criterion.key}
											criterion={criterion}
											coachOptions={coachOptions}
											player={hintPlayer}
											onChange={(patch) => updateCriterion(phase.key, criterion.key, patch)}
											onRemove={() => removeCriterion(phase.key, criterion.key)}
										/>
									))}
								</div>
								<div className="rtp-wz-add-crit-row">
									<Button
										className="rtp-wz-add-crit-btn rtp-wz-add-crit-btn--metric"
										type="dashed"
										size="small"
										icon={<LineChartOutlined />}
										data-attr="settings.rtp-plans.wizard-criterion-add-metric"
										onClick={() => addMetricCriterion(phase.key)}
									>
										Metric
									</Button>
									<Button
										className="rtp-wz-add-crit-btn rtp-wz-add-crit-btn--event"
										type="dashed"
										size="small"
										icon={<FlagOutlined />}
										data-attr="settings.rtp-plans.wizard-criterion-add-event"
										onClick={() => addEventCriterion(phase.key)}
									>
										Event
									</Button>
								</div>
							</div>
						</div>
					))}
					<Button
						className="rtp-wz-add-phase-btn"
						type="dashed"
						block
						data-attr="settings.rtp-plans.wizard-phase-add"
						onClick={addPhase}
					>
						Add phase
					</Button>
					<div className="rtp-wz-footer-total">
						Total: <strong>{totalWeeksLabel} wk</strong>
						{expectedWeeksLabel != null ? ` · expected ${expectedWeeksLabel} wk` : ""}
					</div>
				</div>
			) : null}

			{pane === "staff" ? (
				<div className="rtp-wz-pane">
					<StaffFlagsEditor
						staff={staff}
						onChange={setStaff}
						getName={getStaffName}
						removeDataAttr="settings.rtp-plans.wizard-staff-remove"
					/>
				</div>
			) : null}

			{pane === "review" ? (
				<div className="rtp-wz-pane">
					<div className="rtp-wz-review-row">
						<span>Player</span>
						<strong>{selectedPlayer ? `${selectedPlayer.first_name} ${selectedPlayer.last_name}` : "—"}</strong>
					</div>
					<div className="rtp-wz-review-row">
						<span>Injury</span>
						<strong>{injuryLabel}</strong>
					</div>
					<div className="rtp-wz-review-row">
						<span>Injury date</span>
						<strong>{injuryDate ? injuryDate.format("MMM D, YYYY") : "—"}</strong>
					</div>
					<div className="rtp-wz-review-row">
						<span>Expected duration</span>
						<strong>{expectedWeeksLabel != null ? `${expectedWeeksLabel} wk` : "—"}</strong>
					</div>
					<div className="rtp-wz-review-row">
						<span>Plan name</span>
						<strong>{planName || "—"}</strong>
					</div>
					<div className="rtp-wz-review-row">
						<span>{sourcePlanId != null ? "Copied from" : sourceRow.label}</span>
						<strong>{sourcePlanId != null ? sourceName : sourceRow.value}</strong>
					</div>
					<div className="rtp-wz-review-row">
						<span>Phases</span>
						<strong>
							{phases.length} phase{phases.length === 1 ? "" : "s"} · {totalWeeksLabel} wk total
						</strong>
					</div>
					<div className="rtp-wz-review-row">
						<span>Exit criteria</span>
						<strong>{totalCriteriaCount} total</strong>
					</div>
					<div className="rtp-wz-review-row">
						<span>Staff</span>
						<strong>
							{Object.keys(staff).length} staff
							{managerNames.length ? ` · manager: ${managerNames.join(", ")}` : ""}
						</strong>
					</div>
					<div className="rtp-wz-review-note">
						Exit criteria will populate each phase's clearance checklist. Staff can add more from the plan detail
						page.
					</div>
				</div>
			) : null}
		</Modal>
	);
};
